// Inspection page — FMCSA-compliant walk-around inspections
// Templates: Pre-trip (11-point), Coupling (16-step), Cargo, Annual
// Per 49 CFR 392.7, 396.11, 396.13, 396.17

import { getAllVehicles, getAllTrailers, addInspection } from '../stores/db.js';
import { showToast } from '../app.js';

// ─── INSPECTION TEMPLATES ───────────────────────────────────────────

const PRETIP_STEPS = [
  { name: 'Service Brakes', items: ['Pedal feel and travel', 'Pad/lining thickness adequate', 'Rotors/drums — no cracks', 'Brake lines — no leaks', 'Trailer brake connection tested', 'Air pressure builds to normal'] },
  { name: 'Parking Brake', items: ['Engages fully', 'Holds on grade', 'Release operates correctly'] },
  { name: 'Steering Mechanism', items: ['Wheel play within limits', 'Tie rods secure', 'Power steering fluid level', 'No leaks in steering system', 'Steering shaft secure'] },
  { name: 'Lighting & Reflectors', items: ['Headlights (low and high)', 'Tail lights', 'Turn signals (all four)', 'Brake lights', 'Clearance/marker lights', 'Reflectors intact', 'Hazard lights'] },
  { name: 'Tires', items: ['Tread depth (4/32" steer, 2/32" other)', 'No sidewall damage/bulges', 'Proper inflation', 'No exposed cord/belt', 'Matching tires on same axle', 'DOT date code checked'] },
  { name: 'Horn', items: ['Audible from 200 feet', 'Operates correctly'] },
  { name: 'Windshield Wipers', items: ['All speeds operate', 'Blade contact adequate', 'Washer fluid level', 'No streaking or skipping'] },
  { name: 'Rear Vision Mirrors', items: ['Both present and mounted', 'Properly adjusted', 'No cracks or damage', 'Clean and clear'] },
  { name: 'Coupling Devices', items: ['5th wheel/hitch secure', 'Locking jaws closed on kingpin', 'Release arm in locked position', 'Safety chains crossed under tongue', 'Breakaway cable connected', 'Air and electrical lines connected'] },
  { name: 'Wheels & Rims', items: ['Lug nuts tight (torqued)', 'No cracks in rims', 'No missing lugs', 'No rust trails (indicates loose)', 'Hub oil seal not leaking'] },
  { name: 'Emergency Equipment', items: ['Fire extinguisher charged and mounted', 'Three warning triangles present', 'Spare fuses available'] },
];

const COUPLING_STEPS = [
  { name: 'Inspect Fifth Wheel', items: ['No damaged/missing parts', 'Mounting secure, no frame cracks', 'Plate greased', 'Tilted toward rear of tractor', 'Jaws open', 'Safety handle in auto-lock position'] },
  { name: 'Inspect Area', items: ['Clear of obstacles underneath', 'Ground surface stable'] },
  { name: 'Chock Trailer Wheels', items: ['Wheel chocks in place or spring brakes set'] },
  { name: 'Position Tractor', items: ['Directly in front of trailer', 'NOT at an angle', 'Aligned with kingpin'] },
  { name: 'Back Slowly', items: ['Fifth wheel contacts trailer frame', 'Low reverse gear'] },
  { name: 'Secure Tractor', items: ['Parking brake set', 'Transmission in neutral'] },
  { name: 'Check Trailer Height', items: ['Trailer slightly raised during coupling', 'Nose of trailer not too high or low'] },
  { name: 'Connect Air Lines', items: ['Emergency line first (red)', 'Service line second (blue)', 'Glad hands sealed properly', 'No crossed lines'] },
  { name: 'Supply Air to Trailer', items: ['Push air supply knob in', 'Wait for normal pressure', 'Listen for leaks', 'Check brake application/release'] },
  { name: 'Lock Trailer Brakes', items: ['Pull air supply knob out (emergency)', 'Trailer brakes locked'] },
  { name: 'Back Under Trailer', items: ['Lowest reverse gear', 'Slowly until kingpin locks in fifth wheel'] },
  { name: 'Tug Test', items: ['Raise landing gear slightly', 'Pull forward gently against locked brakes', 'Tractor should not separate'] },
  { name: 'Inspect Coupling', items: ['Flashlight: no space between fifth wheel halves', 'Jaws closed around kingpin shank', 'Locking lever in lock position', 'Safety latch/pin engaged'] },
  { name: 'Connect Electrical', items: ['Plug electrical cord in', 'Safety catch secured', 'Check all trailer lights work', 'Lines clear of moving parts'] },
  { name: 'Raise Landing Gear', items: ['Low gear first until weight-free', 'Switch to high gear', 'Raise completely', 'Crank handle secure', 'Clearance between tractor and gear'] },
  { name: 'Remove Chocks', items: ['All wheel chocks removed', 'Chocks stored securely'] },
];

const CARGO_STEPS = [
  { name: 'Cargo Securement', items: ['Load properly distributed', 'Adequate tie-downs/straps', 'No shifting during transport', 'Weight within limits', 'Tailgate/doors secured'] },
  { name: 'Cargo Visibility', items: ['Does not obstruct driver view', 'Does not interfere with movement of arms/legs', 'Does not block exit'] },
  { name: 'Hazmat Placards', items: ['Correct placards displayed (if applicable)', 'Shipping papers accessible', 'Placards match cargo'] },
];

// ─── RENDER ─────────────────────────────────────────────────────────

export async function renderInspect(container, ctx) {
  const vehicles = await getAllVehicles(ctx.carrierDOT);
  const trailers = await getAllTrailers(ctx.carrierDOT);

  container.innerHTML = `
    <div class="page">
      <div class="page-title">Inspection</div>

      <div class="card">
        <div class="input-group">
          <label>Vehicle</label>
          <select id="inspect-vehicle">
            <option value="">Select truck or trailer...</option>
            ${vehicles.map(v => `<option value="VIN:${v.vin}">${v.year || ''} ${v.make || ''} ${v.model || ''} (${v.vin.slice(-6)})</option>`).join('')}
            ${trailers.map(t => `<option value="TRL:${t.id}">${t.type || 'Trailer'} ${t.unitNumber || t.serialNumber || ''} (${t.plateNumber || 'no plate'})</option>`).join('')}
          </select>
        </div>

        <div class="input-group">
          <label>Current Odometer (trucks only)</label>
          <input type="number" id="inspect-mileage" placeholder="e.g. 127850">
        </div>

        <div class="input-group">
          <label>Inspection Type</label>
          <select id="inspect-type">
            <option value="pretip">Pre-Trip Walk-Around (11 points)</option>
            <option value="coupling">Trailer Coupling (16 steps)</option>
            <option value="cargo">Cargo Securement</option>
          </select>
        </div>

        <button class="btn btn-primary" onclick="window._startInspection()">Start Inspection</button>
      </div>

      <div id="inspection-flow" style="display: none;"></div>
    </div>
  `;

  // ─── Inspection state ─────────────────────────────────────────
  let currentStep = 0;
  let steps = [];
  let inspectionType = '';
  let vehicleId = '';
  let stepResults = [];

  window._startInspection = () => {
    vehicleId = document.getElementById('inspect-vehicle').value;
    if (!vehicleId) { showToast('Select a vehicle'); return; }

    inspectionType = document.getElementById('inspect-type').value;
    const templates = { pretip: PRETIP_STEPS, coupling: COUPLING_STEPS, cargo: CARGO_STEPS };
    steps = templates[inspectionType] || PRETIP_STEPS;
    currentStep = 0;
    stepResults = steps.map(s => ({
      name: s.name,
      verdict: null,
      checkedItems: new Array(s.items.length).fill(false),
      photos: [],
      notes: '',
    }));

    renderStep();
  };

  function renderStep() {
    const flow = document.getElementById('inspection-flow');
    flow.style.display = 'block';

    const step = steps[currentStep];
    const result = stepResults[currentStep];
    const progress = ((currentStep + 1) / steps.length) * 100;

    flow.innerHTML = `
      <div class="card" style="border-color: var(--accent);">
        <div class="inspection-progress"><div class="inspection-progress-fill" style="width: ${progress}%"></div></div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
          <div style="font-weight: 600; color: #fff;">Step ${currentStep + 1}/${steps.length}: ${step.name}</div>
          <div style="color: var(--text-dim); font-size: 0.85em;">${inspectionType.toUpperCase()}</div>
        </div>

        <!-- Checklist -->
        <div style="margin-bottom: 12px;">
          ${step.items.map((item, i) => `
            <div class="checklist-item" onclick="window._toggleCheck(${i})">
              <div class="checklist-check ${result.checkedItems[i] ? 'checked' : ''}">${result.checkedItems[i] ? '✓' : ''}</div>
              <span>${item}</span>
            </div>
          `).join('')}
        </div>

        <!-- Verdict -->
        <div class="verdict-buttons">
          <div class="verdict-btn ${result.verdict === 'pass' ? 'selected-pass' : ''}" onclick="window._setVerdict('pass')">PASS</div>
          <div class="verdict-btn ${result.verdict === 'fail' ? 'selected-fail' : ''}" onclick="window._setVerdict('fail')">FAIL</div>
          <div class="verdict-btn ${result.verdict === 'advisory' ? 'selected-advisory' : ''}" onclick="window._setVerdict('advisory')">ADVISORY</div>
          ${inspectionType === 'pretip' && currentStep === 8 ? `<div class="verdict-btn ${result.verdict === 'na' ? 'selected-na' : ''}" onclick="window._setVerdict('na')">N/A</div>` : ''}
        </div>

        <!-- Photo + Notes -->
        <div class="capture-row">
          <div class="capture-btn" onclick="window._capturePhoto()">📷 Take Photo</div>
          <div class="capture-btn" onclick="window._addNote()">📝 Add Note</div>
        </div>

        ${result.photos.length ? `<div class="photo-strip">${result.photos.map(p => `<img src="${p}" alt="inspection photo">`).join('')}</div>` : ''}
        ${result.notes ? `<div style="font-size: 0.85em; color: var(--text-dim); margin-top: 4px;">Note: ${result.notes}</div>` : ''}

        <!-- Navigation -->
        <div style="display: flex; gap: 8px; margin-top: 16px;">
          ${currentStep > 0 ? '<button class="btn btn-secondary" onclick="window._prevStep()">← Back</button>' : ''}
          ${currentStep < steps.length - 1
            ? `<button class="btn btn-primary" onclick="window._nextStep()" ${!result.verdict ? 'disabled' : ''}>Next →</button>`
            : `<button class="btn btn-primary" onclick="window._completeInspection()" ${!result.verdict ? 'disabled' : ''}>✅ Complete</button>`
          }
        </div>
      </div>
    `;
  }

  window._toggleCheck = (idx) => {
    stepResults[currentStep].checkedItems[idx] = !stepResults[currentStep].checkedItems[idx];
    renderStep();
  };

  window._setVerdict = (v) => {
    stepResults[currentStep].verdict = v;
    renderStep();
  };

  window._prevStep = () => { currentStep--; renderStep(); };
  window._nextStep = () => {
    if (!stepResults[currentStep].verdict) { showToast('Select a verdict first'); return; }
    currentStep++;
    renderStep();
  };

  window._capturePhoto = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          stepResults[currentStep].photos.push(reader.result);
          renderStep();
          showToast('Photo captured');
        };
        reader.readAsDataURL(file);
      };
      input.click();
    } catch (e) {
      showToast('Camera not available');
    }
  };

  window._addNote = () => {
    const note = prompt('Inspection note:');
    if (note) {
      stepResults[currentStep].notes = note;
      renderStep();
    }
  };

  window._completeInspection = async () => {
    if (!stepResults[currentStep].verdict) { showToast('Select a verdict first'); return; }

    const passCount = stepResults.filter(s => s.verdict === 'pass').length;
    const failCount = stepResults.filter(s => s.verdict === 'fail').length;
    const advisoryCount = stepResults.filter(s => s.verdict === 'advisory').length;
    const overallResult = failCount > 0 ? 'fail' : advisoryCount > 0 ? 'advisory' : 'pass';

    const mileage = parseInt(document.getElementById('inspect-mileage')?.value) || 0;

    const inspection = {
      vehicleId,
      type: inspectionType,
      mileage,
      steps: stepResults,
      overallResult,
      passCount,
      failCount,
      advisoryCount,
      inspectorName: ctx.userName,
      carrierDOT: ctx.carrierDOT,
    };

    await addInspection(inspection);

    // Reset flow
    document.getElementById('inspection-flow').style.display = 'none';
    showToast(`Inspection complete: ${overallResult.toUpperCase()} (${passCount}P/${failCount}F/${advisoryCount}A)`);
  };
}
